import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { getCompanyStatuses } from '../utils/statuses';
import PageHeader from './PageHeader';

export default function BulkAssignment({ isDarkMode, currentUserId }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [icoInput, setIcoInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [foundCompanies, setFoundCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  
  // Nastavení pro hromadné přiřazení
  const [nextActionDate, setNextActionDate] = useState('');
  const [nextActionTime, setNextActionTime] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);

  // Načti uživatele při otevření
  useEffect(() => {
    const init = async () => {
      try {
        console.log('🚀 Inicializace BulkAssignment...');
        await fetchUsers();
        await fetchStatuses();
        console.log('✅ Inicializace dokončena');
      } catch (err) {
        console.error('❌ Chyba při inicializaci:', err);
        setError(err.message);
      }
    };
    init();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Načteno uživatelů:', data.length);
        setUsers(data);
      } else {
        console.error('❌ Chyba při načítání uživatelů:', response.status);
      }
    } catch (error) {
      console.error('❌ Chyba při načítání uživatelů:', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const statusObjects = await getCompanyStatuses();
      const statusNames = statusObjects.map(s => s.name);
      console.log('✅ Načteny stavy:', statusNames);
      setAvailableStatuses(statusNames || []);
    } catch (error) {
      console.error('Chyba při načítání stavů:', error);
      setAvailableStatuses([]);
    }
  };

  // Normalizace IČO na 8 číslic (stejně jako v DuplicateChecker)
  const normalizeIco = (ico) => {
    if (!ico) return '';
    return ico.toString().trim().replace(/\D/g, '').padStart(8, '0');
  };

  // Vyhledání firem podle IČO - rychlé vyhledávání pomocí search parametru
  const handleSearchCompanies = async () => {
    if (!icoInput.trim()) {
      alert('Zadejte prosím seznam IČO');
      return;
    }

    setLoading(true);
    setSearchCompleted(false);
    setFoundCompanies([]);

    try {
      // Rozděl IČO podle řádků
      const icoList = icoInput
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      console.log(`🔍 Hledám ${icoList.length} firem podle IČO...`);

      // Vyhledej každé IČO pomocí search endpointu (mnohem rychlejší!)
      const foundCompaniesMap = new Map(); // Použijeme Map pro prevenci duplicit
      
      for (let i = 0; i < icoList.length; i++) {
        const ico = icoList[i];
        const normalizedIco = normalizeIco(ico);
        
        console.log(`🔎 Hledám IČO ${i + 1}/${icoList.length}: ${ico} (normalizováno: ${normalizedIco})`);
        
        try {
          // Vyhledej pomocí search parametru - hledá v IČO i názvu
          const searchUrl = `${API_URL}/companies?search=${encodeURIComponent(ico)}&limit=50`;
          const response = await fetch(searchUrl);
          
          if (response.ok) {
            const data = await response.json();
            const companies = data.companies || data || [];
            
            // Filtruj pouze firmy s přesně odpovídajícím IČO (search může najít i v názvu)
            const exactMatches = companies.filter(company => {
              const companyNormalizedIco = normalizeIco(company.ico);
              return companyNormalizedIco === normalizedIco;
            });
            
            if (exactMatches.length > 0) {
              // Přidej první nalezenou firmu (měla by být jen jedna s daným IČO)
              const company = exactMatches[0];
              foundCompaniesMap.set(normalizedIco, company);
              console.log('✅ Nalezena firma:', company.name, 'IČO:', company.ico);
            } else {
              console.log(`❌ IČO ${ico} nenalezeno`);
            }
          } else {
            console.warn(`⚠️ Chyba při hledání IČO ${ico}: ${response.status}`);
          }
        } catch (error) {
          console.error(`❌ Chyba při hledání IČO ${ico}:`, error);
        }
      }

      // Seřaď podle pořadí v zadaném seznamu IČO
      const sortedFound = [];
      icoList.forEach(ico => {
        const normalizedIco = normalizeIco(ico);
        const company = foundCompaniesMap.get(normalizedIco);
        if (company) {
          sortedFound.push(company);
        }
      });

      setFoundCompanies(sortedFound);
      setSearchCompleted(true);
      console.log(`✅ Nalezeno ${sortedFound.length} z ${icoList.length} firem`);

      if (sortedFound.length === 0) {
        alert('Nebyly nalezeny žádné firmy s danými IČO');
      } else if (sortedFound.length < icoList.length) {
        alert(`Nalezeno ${sortedFound.length} z ${icoList.length} firem.\nNěkterá IČO nebyla v databázi nalezena.`);
      }

    } catch (error) {
      console.error('❌ CHYBA při hledání firem:', error);
      alert('Chyba při hledání firem: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Přidělení kontaktů novému vlastníkovi
  const handleBulkAssign = async () => {
    if (!selectedUserId) {
      alert('Vyberte prosím nového vlastníka');
      return;
    }

    if (foundCompanies.length === 0) {
      alert('Nejsou k dispozici žádné firmy k přiřazení');
      return;
    }

    // Zpracuj poznámky - rozděl podle řádků
    const notesList = notesInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // Validace: poznámky MUSÍ být, a musí jich být stejně jako firem
    if (notesList.length === 0) {
      alert('Zadejte prosím poznámky k aktivitám (každá firma musí mít poznámku)');
      return;
    }

    if (notesList.length !== foundCompanies.length) {
      alert(`Počet poznámek (${notesList.length}) se neshoduje s počtem firem (${foundCompanies.length}).\n\nZadejte přesně ${foundCompanies.length} poznámek - každá na nový řádek.`);
      return;
    }

    // Validace: pokud je vyplněno datum, musí být i čas (a naopak)
    const hasDate = nextActionDate && nextActionDate.trim();
    const hasTime = nextActionTime && nextActionTime.trim();
    if ((hasDate && !hasTime) || (!hasDate && hasTime)) {
      alert('Pro nastavení data další aktivity zadejte datum i čas');
      return;
    }

    const confirmMessage = `Opravdu chcete přiřadit ${foundCompanies.length} firem novému vlastníkovi?
\n- Budou přidány individuální poznámky k aktivitám
${hasDate && hasTime ? `\n- Bude nastaveno datum další aktivity: ${nextActionDate} ${nextActionTime}` : ''}
${selectedStatus ? `\n- Bude nastaven stav: ${selectedStatus}` : ''}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setAssigning(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      // Vytvoř ISO time string z data a času
      let nextActionISO = null;
      if (hasDate && hasTime) {
        const [year, month, day] = nextActionDate.split('-').map(Number);
        const [hours, minutes] = nextActionTime.split(':').map(Number);
        const localDateTime = new Date(year, month - 1, day, hours, minutes);
        nextActionISO = localDateTime.toISOString();
      }

      for (let i = 0; i < foundCompanies.length; i++) {
        const company = foundCompanies[i];
        const note = notesList[i]; // Vždy existuje (validováno výše)

        try {
          // Připrav data pro update
          const updateData = {
            ...company,
            createdBy: selectedUserId,
          };

          // Vytvoř aktivitu s poznámkou
          const newActivity = {
            text: note,
            createdAt: new Date().toISOString(),
            createdBy: currentUserId,
          };

          if (nextActionISO) {
            newActivity.nextActionDate = nextActionISO;
          }

          if (selectedStatus) {
            newActivity.status = selectedStatus;
          }

          updateData.activities = [...(company.activities || []), newActivity];

          // Aktualizuj datum další aktivity a stav pokud jsou nastavené
          if (nextActionISO) {
            updateData.nextActionDate = nextActionISO;
          }

          if (selectedStatus) {
            updateData.status = selectedStatus;
          }

          // Odešli update
          const response = await fetch(`${API_URL}/companies/${company._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'x-user-id': currentUserId,
              'x-transfer-type': 'bulk_assignment'
            },
            body: JSON.stringify(updateData)
          });

          if (response.ok) {
            successCount++;
            console.log(`✅ Firma ${i + 1}/${foundCompanies.length} přiřazena`);
          } else {
            errorCount++;
            console.error(`❌ Chyba při přiřazení firmy ${company.name}: ${response.statusText}`);
          }

        } catch (error) {
          errorCount++;
          console.error(`❌ Chyba při zpracování firmy ${company.name}:`, error);
        }
      }

      alert(`Hromadné přiřazení dokončeno!\n✅ Úspěšně: ${successCount}\n❌ Chyby: ${errorCount}`);

      // Návrat na správu kontaktů po úspěšném přiřazení
      if (successCount > 0) {
        navigate('/contact-management');
      }

    } catch (error) {
      console.error('Chyba při hromadném přiřazení:', error);
      alert('Chyba při hromadném přiřazení: ' + error.message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10">
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
      
      {/* Page Header */}
      <PageHeader
        title="🔄 Hromadné přidělování kontaktů"
        isDarkMode={isDarkMode}
        actions={
          <button
            onClick={() => navigate('/contact-management')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            ← Zpět na správu kontaktů
          </button>
        }
      />

      {/* Main Content */}
      <div className="w-full">
        
        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
            <h3 className="font-bold">Chyba při načítání</h3>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-white text-red-500 rounded"
            >
              Obnovit stránku
            </button>
          </div>
        )}
        
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          
          {/* Krok 1: Výběr uživatele */}
          <div className="mb-6">
            <label className="block font-semibold mb-2">1️⃣ Vyberte nového vlastníka:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className={`w-full p-3 border rounded-lg ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">-- Vyberte uživatele --</option>
              {users && users.map(user => (
                <option key={user._id} value={user._id}>
                  {String(user.name || user.username || 'Uživatel')}
                </option>
              ))}
            </select>
          </div>

          {/* Krok 2: Zadání IČO */}
          <div className="mb-6">
            <label className="block font-semibold mb-2">2️⃣ Zadejte seznam IČO (každé na nový řádek):</label>
            <textarea
              value={icoInput}
              onChange={(e) => setIcoInput(e.target.value)}
              placeholder="Např.&#10;12345678&#10;87654321&#10;11223344"
              rows={8}
              className={`w-full p-3 border rounded-lg font-mono ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              onClick={handleSearchCompanies}
              disabled={loading || !icoInput.trim()}
              className={`mt-2 px-6 py-2 rounded-lg font-semibold transition-colors ${
                loading || !icoInput.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? '🔍 Hledám firmy...' : '🔍 Vyhledat firmy'}
            </button>
          </div>

          {/* Výsledky vyhledávání */}
          {searchCompleted && (
            <>
              <div className="mb-6">
                <h3 className="font-semibold mb-2">
                  📋 Nalezené firmy ({foundCompanies.length}):
                </h3>
                {foundCompanies.length > 0 ? (
                  <div className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <table className="w-full">
                      <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                        <tr>
                          <th className="px-4 py-2 text-left">#</th>
                          <th className="px-4 py-2 text-left">IČO</th>
                          <th className="px-4 py-2 text-left">Název</th>
                          <th className="px-4 py-2 text-left">Aktuální vlastník</th>
                          <th className="px-4 py-2 text-left">Stav</th>
                        </tr>
                      </thead>
                      <tbody>
                        {foundCompanies.map((company, index) => {
                          // Bezpečné získání hodnot
                          const ico = company.ico || '';
                          const name = company.name || 'Bez názvu';
                          const owner = typeof company.createdBy === 'object' 
                            ? (company.createdBy?.name || company.createdBy?.username || 'Neznámý')
                            : 'Neznámý';
                          const status = company.status || 'Bez stavu';
                          
                          return (
                            <tr
                              key={company._id || index}
                              className={`border-t ${
                                isDarkMode
                                  ? 'border-gray-600 hover:bg-gray-700'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <td className="px-4 py-2">{index + 1}</td>
                              <td className="px-4 py-2 font-mono">{String(ico)}</td>
                              <td className="px-4 py-2">{String(name)}</td>
                              <td className="px-4 py-2">{String(owner)}</td>
                              <td className="px-4 py-2">{String(status)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-red-500">Nebyly nalezeny žádné firmy</p>
                )}
              </div>

              {/* Krok 3: Poznámky */}
              {foundCompanies.length > 0 && (
                <>
                  <div className="mb-6">
                    <label className="block font-semibold mb-2">
                      3️⃣ Poznámky k aktivitám (každá na nový řádek ve stejném pořadí jako IČO):
                    </label>
                    <textarea
                      value={notesInput}
                      onChange={(e) => setNotesInput(e.target.value)}
                      placeholder={`Zadejte poznámky v pořadí firem:\nPoznámka pro firmu 1\nPoznámka pro firmu 2\nPoznámka pro firmu 3\n...\n\nPočet poznámek: ${foundCompanies.length}`}
                      rows={Math.min(foundCompanies.length + 2, 15)}
                      className={`w-full p-3 border rounded-lg ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <p className="text-sm mt-1 opacity-70">
                      💡 Tip: Zadejte přesně {foundCompanies.length} poznámek (každá na nový řádek)
                    </p>
                  </div>

                  {/* Krok 4: Datum a čas další aktivity */}
                  <div className="mb-6">
                    <label className="block font-semibold mb-2">
                      4️⃣ Datum a čas další aktivity (nastaví se všem):
                    </label>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-sm mb-1">Datum:</label>
                        <input
                          type="date"
                          value={nextActionDate}
                          onChange={(e) => setNextActionDate(e.target.value)}
                          className={`w-full p-3 border rounded-lg ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm mb-1">Čas:</label>
                        <input
                          type="time"
                          value={nextActionTime}
                          onChange={(e) => setNextActionTime(e.target.value)}
                          className={`w-full p-3 border rounded-lg ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Krok 5: Stav */}
                  <div className="mb-6">
                    <label className="block font-semibold mb-2">
                      5️⃣ Stav (nastaví se všem):
                    </label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className={`w-full p-3 border rounded-lg ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="">-- Ponechat beze změny --</option>
                      {availableStatuses && availableStatuses.length > 0 ? (
                        availableStatuses.map((status, idx) => (
                          <option key={idx} value={status}>
                            {String(status)}
                          </option>
                        ))
                      ) : (
                        <option disabled>Načítání stavů...</option>
                      )}
                    </select>
                  </div>

                  {/* KONTROLNÍ TABULKA */}
                  {notesInput.trim() && selectedUserId && (
                    <>
                      <div className="mb-6">
                        <h3 className="font-semibold mb-3 text-lg">
                          ✅ Kontrola před přiřazením:
                        </h3>
                        <div className={`border rounded-lg overflow-auto ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <table className="w-full text-sm">
                            <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                              <tr>
                                <th className="px-3 py-2 text-left">#</th>
                                <th className="px-3 py-2 text-left">IČO</th>
                                <th className="px-3 py-2 text-left">Název firmy</th>
                                <th className="px-3 py-2 text-left">Aktivita (poznámka)</th>
                                <th className="px-3 py-2 text-left">Datum další aktivity</th>
                                <th className="px-3 py-2 text-left">Stav</th>
                                <th className="px-3 py-2 text-left">Nový vlastník</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const notesList = notesInput
                                  .split('\n')
                                  .map(line => line.trim())
                                  .filter(line => line.length > 0);
                                
                                const selectedUserObj = users.find(u => u._id === selectedUserId);
                                const newOwnerName = selectedUserObj ? String(selectedUserObj.name || selectedUserObj.username) : 'Neznámý';
                                
                                const dateTimeStr = nextActionDate && nextActionTime 
                                  ? `${nextActionDate} ${nextActionTime}`
                                  : 'Nenastaveno';
                                
                                return foundCompanies.map((company, index) => {
                                  const note = notesList[index] || '(žádná poznámka)';
                                  const hasNote = notesList[index];
                                  
                                  return (
                                    <tr
                                      key={company._id || index}
                                      className={`border-t ${
                                        !hasNote 
                                          ? (isDarkMode ? 'bg-red-900/20' : 'bg-red-100')
                                          : (isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50')
                                      }`}
                                    >
                                      <td className="px-3 py-2">{index + 1}</td>
                                      <td className="px-3 py-2 font-mono">{String(company.ico || '')}</td>
                                      <td className="px-3 py-2">{String(company.name || 'Bez názvu')}</td>
                                      <td className={`px-3 py-2 ${!hasNote ? 'text-red-500 font-semibold' : ''}`}>
                                        {note}
                                      </td>
                                      <td className="px-3 py-2">{dateTimeStr}</td>
                                      <td className="px-3 py-2">{selectedStatus || '(beze změny)'}</td>
                                      <td className="px-3 py-2 font-semibold">{newOwnerName}</td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                        {(() => {
                          const notesList = notesInput.split('\n').filter(l => l.trim().length > 0);
                          if (notesList.length !== foundCompanies.length) {
                            return (
                              <div className="mt-3 p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg">
                                <p className="text-yellow-600 dark:text-yellow-400 font-semibold">
                                  ⚠️ Pozor: Máte {notesList.length} poznámek, ale {foundCompanies.length} firem!
                                  {notesList.length < foundCompanies.length && ' Některé firmy nebudou mít poznámku.'}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
          
          {/* Tlačítko přiřazení */}
          {foundCompanies.length > 0 && (
            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => navigate('/contact-management')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-900'
                }`}
              >
                Zrušit
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={assigning || !selectedUserId}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  assigning || !selectedUserId
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {assigning ? '⏳ Přiděluji...' : `✅ Přiřadit ${foundCompanies.length} firem`}
              </button>
            </div>
          )}
          
        </div>
      </div>
      </div>
    </div>
  );
}
